/* eslint-disable import/extensions */
/* eslint-disable import/no-unresolved */
import React, { useState } from 'react';
import _ from 'underscore';
import PropTypes from 'prop-types';
import { Stack } from '@fluentui/react';
import {
  Caption1Stronger,
  Caption1,
  makeStyles,
  FluentProvider,
  webLightTheme,
} from '@fluentui/react-components';
import { useDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { Panel } from '@bingads-webui-campaign-react/panel';
import { ComponentSettings, ThemeFluent } from '@bingads-webui-theme-2018/theme-fluent';
import { ThemeProvider } from 'react-jss';
import ReactDOM from 'react-dom';
import { UiBlocker } from '@bingads-webui-react/ui-blocker';
import { edmCampaign } from '@bingads-webui/edm-campaign';

import { Notification } from './notification';
import { EmptyView } from './empty-view';
import { BudgetDetail } from './budget-detail';
import { BidDetail } from './bid-detail';
import { getPanelStyles } from './style/style';
import {
  PERFORMANCE_INSIGHTS_ISSUE,
  PERFORMANCE_INSIGHTS_PANEL_CONTAINER,
  RADIO_VALUE,
  EXTERNAL_LINK,
} from './constants/consts';

const useStyles = makeStyles({
  inlineChart: {
    width: '439px',
    height: '290px',
  },
});

const campaignEDM = edmCampaign.types.resolve('Model.Campaign');
const campaignResource = new campaignEDM.ResourceIdentifier();
const bulkEditCampaigns = (data) => {
  const updateCall = campaignResource.bulkUpdate.bind(campaignResource);
  return updateCall(data);
};

export const PerformanceInsightsIssuePanel = (props) => {
  const {
    allData,
    newI18n,
    entity,
    refreshGrid,
    scope,
    issueOrder,
  } = props;
  const [isBlocking, setIsBlocking] = useState(false);
  const [isPanelOpen, setIsPanelOpen] = useState(true);
  const [isBudgetLikeSelected, setIsBudgetLikeSelected] = useState(false);
  const [isBudgetDislikeSelected, setIsBudgetDislikeSelected] = useState(false);
  const [isBidLikeSelected, setIsBidLikeSelected] = useState(false);
  const [isBidDislikeSelected, setIsBidDislikeSelected] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [isError, setIsError] = useState(false);
  const [applyStatus, setApplyStatus] = useState({
    [PERFORMANCE_INSIGHTS_ISSUE.BUDGET]: false,
    [PERFORMANCE_INSIGHTS_ISSUE.BID]: false,
  });
  const [radioValue, setRadioValue] = useState(RADIO_VALUE.suggest);
  const [currentApplyType, setCurrentApplyType] = useState(null);
  const onCancel = () => {
    ReactDOM.unmountComponentAtNode(document.querySelector(PERFORMANCE_INSIGHTS_PANEL_CONTAINER));
    setIsPanelOpen(false);
    if (_.some(applyStatus, status => status)) {
      refreshGrid();
    }
  };
  const classes = useDefaultStyles(getPanelStyles);
  const chartClasses = useStyles();

  const handleSaveBudgetSuccess = () => {
    setIsSuccess(true);
    setApplyStatus({
      ...applyStatus,
      [PERFORMANCE_INSIGHTS_ISSUE.BUDGET]: true,
    });
  };
  const handleSaveBudgetError = () => {
    setIsError(true);
  };
  const handleSaveBidSuccess = () => {
    setIsSuccess(true);
    setApplyStatus({
      ...applyStatus,
      [PERFORMANCE_INSIGHTS_ISSUE.BID]: true,
    });
  };
  const handleSaveBidError = () => {
    setIsError(true);
  };
  const beforeSearch = () => {
    setIsBlocking(true);
    setIsSuccess(false);
    setIsError(false);
  };
  const onClickSaveBudget = () => {
    beforeSearch();
    setCurrentApplyType(PERFORMANCE_INSIGHTS_ISSUE.BUDGET);
    bulkEditCampaigns([{
      Id: entity.Id,
      Budget: {
        Amount: allData[PERFORMANCE_INSIGHTS_ISSUE.BUDGET].recommendedBudget,
      },
    }]).then(() => {
      handleSaveBudgetSuccess();
    }).catch((response) => {
      handleSaveBudgetError(response);
    }).finally(() => setIsBlocking(false));
  };
  const onClickSaveBid = () => {
    beforeSearch();
    setCurrentApplyType(PERFORMANCE_INSIGHTS_ISSUE.BID);
    let suggestBid;
    if (radioValue === RADIO_VALUE.suggest) {
      suggestBid = allData[PERFORMANCE_INSIGHTS_ISSUE.BID].latestSuggestTarget;
    } else if (radioValue === RADIO_VALUE.vertical) {
      suggestBid = allData[PERFORMANCE_INSIGHTS_ISSUE.BID].verticalTarget;
    }
    bulkEditCampaigns([{
      Id: entity.Id,
      BiddingScheme: {
        '@odata.type': '#Model.TargetCpaBiddingScheme',
        TargetCpa: suggestBid,
        Type: 'TargetCpa',
      },
    }]).then(() => {
      handleSaveBidSuccess();
    }).catch((response) => {
      handleSaveBidError(response);
    }).finally(() => setIsBlocking(false));
  };
  const onClickRetry = () => {
    if (currentApplyType === PERFORMANCE_INSIGHTS_ISSUE.BUDGET) {
      onClickSaveBudget();
    } else if (currentApplyType === PERFORMANCE_INSIGHTS_ISSUE.BID) {
      onClickSaveBid();
    }
  };
  const onNavigate = () => {
    ReactDOM.unmountComponentAtNode(document.querySelector(PERFORMANCE_INSIGHTS_PANEL_CONTAINER));
    setIsPanelOpen(false);
    scope.history.push({
      pathname: EXTERNAL_LINK.RECOMMENDATION,
      search: scope.history.location.search,
    });
  };
  return (
    <Panel
      isOpen={isPanelOpen}
      onClose={onCancel}
      i18n={newI18n}
      panelClassName="side-panel-performance-insights"
      title={<Caption1Stronger>{newI18n.getString(_TL_('Performance insights for {{campaignName}}'), { campaignName: entity.Name })}</Caption1Stronger>}
      className={classes}
    >
      <ThemeProvider theme={{ components: ComponentSettings, core: ThemeFluent }}>
        <FluentProvider theme={webLightTheme} className={classes.flexContainer}>
          <UiBlocker
            blocking={isBlocking}
            loadingText={newI18n.getString(_TL_('Loading'))}
          >
            <Stack className={classes.main}>
              <Notification
                isSuccess={isSuccess}
                isError={isError}
                newI18n={newI18n}
                allData={allData}
                entity={entity}
                setIsSuccess={setIsSuccess}
                setIsError={setIsError}
                onClickRetry={onClickRetry}
                classes={classes}
              />
              {_.every(applyStatus, status => status) ? (
                <EmptyView
                  classes={classes}
                  newI18n={newI18n}
                  onNavigate={onNavigate}
                />
              ) : (
                <>
                  <Caption1 className={classes.description}>
                    {newI18n.getString(_TL_('We identified issues within your campaign that may be affecting its performance. Please address the recommended changes listed below to improve its effectiveness.'))}
                  </Caption1>
                  {_.map(issueOrder, (issueType) => {
                    if (issueType === PERFORMANCE_INSIGHTS_ISSUE.BUDGET && !applyStatus[PERFORMANCE_INSIGHTS_ISSUE.BUDGET]) {
                      return (
                        <BudgetDetail
                          classes={classes}
                          newI18n={newI18n}
                          allData={allData}
                          onClickSaveBudget={onClickSaveBudget}
                          setIsBudgetLikeSelected={setIsBudgetLikeSelected}
                          setIsBudgetDislikeSelected={setIsBudgetDislikeSelected}
                          isBudgetLikeSelected={isBudgetLikeSelected}
                          isBudgetDislikeSelected={isBudgetDislikeSelected}
                          chartClasses={chartClasses}
                        />
                      );
                    }
                    if (issueType === PERFORMANCE_INSIGHTS_ISSUE.BID && !applyStatus[PERFORMANCE_INSIGHTS_ISSUE.BID]) {
                      return (
                        <BidDetail
                          classes={classes}
                          newI18n={newI18n}
                          allData={allData}
                          onClickSaveBid={onClickSaveBid}
                          setIsBidLikeSelected={setIsBidLikeSelected}
                          setIsBidDislikeSelected={setIsBidDislikeSelected}
                          isBidLikeSelected={isBidLikeSelected}
                          isBidDislikeSelected={isBidDislikeSelected}
                          chartClasses={chartClasses}
                          setRadioValue={setRadioValue}
                          radioValue={radioValue}
                        />
                      );
                    }
                    return null;
                  })}
                </>
              )}
            </Stack>
          </UiBlocker>
        </FluentProvider>
      </ThemeProvider>
    </Panel>
  );
};
PerformanceInsightsIssuePanel.propTypes = {
  allData: PropTypes.shape({}).isRequired,
  newI18n: PropTypes.shape({
    getString: PropTypes.func.isRequired,
  }).isRequired,
  entity: PropTypes.shape({
    Name: PropTypes.string,
    Id: PropTypes.number,
  }).isRequired,
  refreshGrid: PropTypes.func.isRequired,
  scope: PropTypes.shape({
    history: PropTypes.shape({
      push: PropTypes.func.isRequired,
      location: PropTypes.shape({
        search: PropTypes.string.isRequired,
      }).isRequired,
    }).isRequired,
  }).isRequired,
  issueOrder: PropTypes.arrayOf(PropTypes.string).isRequired,
};
